import type { Mods } from '../sim/stats';
/* ============================================================
   破格協定（傳說級選項，2026-08-04 P1）
   ============================================================

   `03-in-run-progression.md` 的稀有度表要求「傳說 = 質變／破格效果」，並且
   **12 分鐘後每次抽卡至少 25% 機率出現一個非線性選項**。那條規則是用來治
   毒性殺手 #7（build 定型後選項退化成「+2% 攻速」）的。

   問題是池子裡原本**沒有任何傳說級內容**：10 把武裝與 27 個被動全都是
   線性加成或規則效果，而進化與融合在 2026-08-04 已改成只從寶箱出。
   使用者拍板「P1 順便做 4–6 個傳說破格選項」，這個檔就是那批東西。

   ## 三條設計約束（違反任一條就會變成「純加強」而不是「破格」）

   1. **一定要有代價。** 沒有代價的質變只是更大的數字，玩家不會思考，
      而 `08-antipatterns.md` 的 #9 說「只提升 30% 玩家根本不知道自己進化了」——
      反過來，只給好處的 ×1.75 也只是把所有場次推向同一個正解。
   2. **不佔被動槽，但一場最多兩個**（`PROTOCOL_MAX`）。佔槽的話 12 分鐘後
      六格早滿，`canTake` 會把它們整批濾掉 —— 那正是它們最該出現的時間點，
      這個機制會變成靜默的死碼。上限 2 讓「拿哪兩個」本身是取捨。
   3. **每一條都要對得上一個真實的消費點。** 這裡用到的六個欄位
      （armor / area / crit / imp / shardKill / lifesteal）都已經有程式在讀，
      不是「玩家看得到但感覺不到的假選擇」（見 `world/player.ts` 的加軸原則）。

   ## 為什麼不做成被動的第 6 級

   被動的等級是連續加成，而這些是**離散的方向宣告**。塞進同一個結構會讓
   「我這一場是玻璃砲流」退化成「我的傷害被動比較高」——
   那個差別正是 `04-build-system.md` 所謂質變與量變的分界。 */

export type Protocol = {
  id: string; n: string; kind: string;
  axes: string[];
  /** 給的是什麼。卡面的主描述。 */
  d: (lv: number) => string;
  /** 代價。卡面上獨立一行、紅字 —— **看不到代價的破格效果等於沒有代價**。 */
  cost: string;
  mods: (lv: number) => Mods;
};

/** 一場最多持有幾個。**吝嗇是刻意的**：三個以上就湊得出「全都要」，
    而破格協定之間的互斥（脆 vs 硬、快 vs 重）正是它們有趣的地方。 */
export var PROTOCOL_MAX = 2;

export var PROTOCOLS: Protocol[] = [
  {
    /* 最直白的一條，放第一個是為了讓玩家第一次看到「協定」這類卡時
       一眼就懂它的形狀：一個大的好處配一個大的壞處。 */
    id: 'x-glass', n: '玻璃砲協定', kind: '協定', axes: ['傷害', '結構'],
    d: function () { return '全武裝傷害 **×1.75**'; },
    cost: '受到的傷害 ×1.9',
    mods: function () {
      return { mul: { atkDmg: 1.75, midDmg: 1.75, ultDmg: 1.75, armor: 1.9 } };
    }
  },
  {
    /* 暴擊改的是傷害的**分布**（見 p-crit 的註解）。推到必爆之後，
       分布這件事就消失了 —— 換來的是把範圍砍掉一半，清場能力整個轉成單體。 */
    id: 'x-focus', n: '聚焦協定', kind: '協定', axes: ['傷害', '範圍'],
    d: function () { return '暴擊率 **+95%**、暴擊傷害 +60%'; },
    cost: '所有爆炸與掃描範圍 ×0.55',
    mods: function () {
      return { add: { crit: 0.95, critMul: 0.6 }, mul: { area: 0.55 } };
    }
  },
  {
    /* 「快但輕」。跟玻璃砲疊起來是 ×1.19 的傷害配上兩份代價 ——
       數學上不划算，但節奏完全不同，這種組合就是 build 有記憶點的來源。 */
    id: 'x-tempo', n: '超頻協定', kind: '協定', axes: ['射速', '冷卻'],
    d: function () { return '主武裝射速 **×1.6**、兩個主動技冷卻 **−45%**'; },
    cost: '全武裝傷害 ×0.68',
    mods: function () {
      return {
        mul: {
          atkRate: 1.6, midCdMax: 0.55, ultCdMax: 0.55,
          atkDmg: 0.68, midDmg: 0.68, ultDmg: 0.68
        }
      };
    }
  },
  {
    /* 衝擊軸的極端化。它與密度反向（見 `data/passives.ts` 檔頭），
       所以代價選的是範圍與移速 —— 打硬目標的人本來就站得比較久。 */
    id: 'x-siege', n: '攻堅協定', kind: '協定', axes: ['衝擊', '機動'],
    d: function () { return '衝擊累積 **×2.4**、踉蹌期間傷害 ×1.5'; },
    cost: '範圍 ×0.7、移動速度 ×0.85',
    mods: function () {
      return { mul: { imp: 2.4, stagDmg: 1.5, area: 0.7, speed: 0.85 } };
    }
  },
  {
    /* 擊殺 → 爆炸 → 再擊殺。雜魚海裡會連鎖，而射程砍掉三成表示
       你得站進那片海裡才引得動 —— 好處與代價指向同一個決定（要不要靠近）。 */
    id: 'x-reap', n: '收割協定', kind: '協定', axes: ['範圍', '傷害'],
    d: function () { return '擊殺時在屍體上引爆 **28 點範圍傷害**（會連鎖一層）'; },
    cost: '主武裝射程 ×0.7',
    mods: function () {
      /* 55 → 28（2026-08-04 五種子量測）。第一版連 `SHARD_CHAIN_FALLOFF`
         都沒有，`p-shard` 一個來源吃掉 59.6% 的總傷害 —— 遠超 07 的 40% 紅線。
         連鎖是指數的，所以這個數字**不能憑感覺調**，改了要重跑 `pnpm metrics`。 */
      return { add: { shardKill: 28 }, mul: { atkRange: 0.7 } };
    }
  },
  {
    /* 把「回血」從時間軸換到擊殺軸：不打就不回。它讓「後退回血」這個
       最無聊的解法失效，逼玩家往人多的地方走 —— 這是行為改變不是數值改變。 */
    id: 'x-leech', n: '汲取協定', kind: '協定', axes: ['結構', '規則'],
    d: function () { return '每次擊殺回復 **1.4 點結構**'; },
    cost: '自然回復歸零、受到的傷害 ×1.3',
    mods: function () {
      return { add: { lifesteal: 1.4 }, mul: { regen: 0, armor: 1.3 } };
    }
  }
];

var byId: Record<string, Protocol> = {};
for (var i = 0; i < PROTOCOLS.length; i++) byId[PROTOCOLS[i].id] = PROTOCOLS[i];
export function protocolById(id): Protocol | null { return byId[id] || null; }
